import React, { useMemo } from 'react';
import { Trophy } from 'lucide-react';
import { RaceStatus, TokenStats } from '../../../../types/race';
import { useEnvAddresses } from '../../../../hooks/useEnvAddresses';

import './LeaderBoard.css';
import { formatTokenAmount } from '../../../../utils/formatTokenAmount';

type LeaderBoardProps = {
  raceStatus: RaceStatus;
  totalPool: bigint;
  winningToken: TokenStats;
  creatorFeePercent: bigint;
  participants: {
    [bettorId: string]: {
      [tokenId: string]: {
        amount: bigint;
        lastTimestamp: bigint;
      };
    };
  };
};

const LeaderBoard = ({ participants, raceStatus, totalPool, winningToken, creatorFeePercent }: LeaderBoardProps) => {
  const { usdc } = useEnvAddresses();

  const formatAddress = (address: string) => {
    return address.slice(0, 4) + '...' + address.slice(-4);
  };

  const topBettors = useMemo(() => {
    const bets = Object.entries(participants).flatMap(([bettorId, tokens]) =>
      Object.entries(tokens).map(([tokenId, bet]) => ({
        address: formatAddress(bettorId),
        avatar: `https://effigy.im/a/${bettorId}.svg`,
        bet: bet.amount,
        tokenId,
      }))
    );

    return bets.sort((a, b) => Number(b.bet - a.bet));
  }, [participants]);

  const getRankStyle = (rank: number) => {
    switch (rank) {
      case 1:
        return {
          backgroundColor: 'rgba(255, 215, 0, 0.1)',
        };
      case 2:
        return {
          backgroundColor: 'rgba(192, 192, 192, 0.1)',
        };
      case 3:
        return {
          backgroundColor: 'rgba(205, 127, 50, 0.1)',
        };
      default:
        return {};
    }
  };

  if (raceStatus === RaceStatus.BETTING) {
    return <div className="leaderboard-empty">Race not started yet</div>;
  }

  if (!Object.keys(participants).length) {
    return <div className="leaderboard-empty">No participants yet</div>;
  }

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-list flex flex-col gap-3">
        {topBettors.map((bettor, index) => {
          const bets = Number(formatTokenAmount(bettor.bet, usdc.decimals, 'usdc'));
          const reward =
            (Number(
              formatTokenAmount((totalPool * (BigInt(1e18) - creatorFeePercent)) / BigInt(1e18), usdc.decimals, 'usdc')
            ) /
              Number(formatTokenAmount(winningToken.totalBetsAmount, usdc.decimals, 'usdc'))) *
            bets;
          return (
            <div
              key={`${bettor.address}-${bettor.tokenId}`}
              className="leaderboard-item"
              style={getRankStyle(index + 1)}
            >
              <div className="rank-info">
                {index < 3 ? (
                  <Trophy size={16} className={`trophy rank-${index + 1}`} />
                ) : (
                  <span className="rank-number">#{index + 1}</span>
                )}
              </div>

              <div className="bettor-info">
                <div className="bettor-avatar">
                  <img src={bettor.avatar} alt={`Rank ${index + 1}`} loading="lazy" />
                </div>
                <div className="bettor-details">
                  <span className="bettor-address">{bettor.address}</span>
                  <div className="bet-info">
                    <span className="bet-amount">
                      ${`${bets} + $${(reward - bets).toFixed(2).replace(/\.?0+$/, '')}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeaderBoard;
